import React, { useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import { useAuth } from "../../../firebase/AuthProvider";
import { useNavigate } from "react-router-dom";
import { publishTemplateFile } from "../../../services/fileServices/FileService";
import { getFileLKValues } from "../../../services/fileServices/FileLKValuesService";
import { FileType } from "../../../interfaces/task/TaskFiles/FileType";
import NewTemplateFile from "../../../interfaces/task/TaskFiles/NewTemplateFile";
import { Button } from "react-bootstrap";
import backarrowbtn from '../../../assets/images/backarrowbtn.svg';
import { Form } from "../../../interfaces/dynamicForms/Forms";
import { GetFormReferences } from "../../../services/formsService/FormsService";
import { toast } from "react-toastify";

interface OptionType {
  value: string;
  label: string;
}

const AddTemplateFile: React.FC = () => {
  const { currentUser, currentOrganization } = useAuth();
  const navigate = useNavigate();
  const [fileTypeOptions, setFileTypeOptions] = useState<OptionType[]>([]);
  const [formOptions, setFormOptions] = useState<OptionType[]>([]);
  const [newTemplate, setNewTemplate] = useState<NewTemplateFile>({
    fileName: "",
    typeId: "",
    formId: "",
  });

  const [lkOptions, setLkOptions] = useState<FileType[] | null | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    // Fetch the task with taskId and update the state
    const fetchLkValues = async () => {
      if (currentUser !== null) {
        const authToken: string = await currentUser.getIdToken();
        const fetchedValues: FileType[] | null = await getFileLKValues(
          authToken
        );
        setLkOptions(fetchedValues);
      }
    };

    if (lkOptions) {
      const formattedFileTypeOptions = lkOptions.map((type) => ({
        value: type.id.toString(),
        label: type.type,
      }));

      setFileTypeOptions(formattedFileTypeOptions);
    }
    if (!lkOptions) {
      fetchLkValues();
    }
  }, [lkOptions]);

  useEffect(() => {
    const fetchForms = async () => {
        if (currentUser) {
            try {
                const token = await currentUser.getIdToken();
                const response = await GetFormReferences(token);
                if (response) { 
                    const options = response.map((form: Form) => ({
                        value: form.id.toString(),
                        label: form.name,
                    }));
                    setFormOptions(options);
                } else {
                    console.error("Failed to fetch forms or response is not as expected.");
                    toast.error("Failed to load available forms.");
                }
            } catch (error) {
                console.error("Error fetching forms:", error);
                toast.error("Failed to load available forms.");
            }
        }
    };

    fetchForms();
}, [currentUser]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setNewTemplate({ ...newTemplate, [name]: value });
  };

  const handleTemplateFileTypeChange = (
    selectedOption: SingleValue<OptionType>
  ) => {
    const typeId = selectedOption ? selectedOption.value : "";
    setNewTemplate({ ...newTemplate, typeId });
  };

  const handleFormChange = (
    selectedOption: SingleValue<OptionType>
  ) => {
    const formId = selectedOption ? selectedOption.value : "";
    if (formId) {
      const pdfOption = fileTypeOptions.find(option => option.label.toLowerCase() === "pdf");
      setNewTemplate({
        ...newTemplate,
        formId,
        typeId: pdfOption ? pdfOption.value : "", // Set to PDF's id
      });
    } else {
      setNewTemplate({ ...newTemplate, formId: "", typeId: "" });
    }
  };

  const readyToSubmit = () => {
    if (newTemplate.fileName === "") {
      toast.error("Filename is required to submit");
      return false;
    }
    if (newTemplate.typeId === "") {
      toast.error("File Type is required to submit");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (readyToSubmit()) {
      setLoading(true); // Start loading
      if (currentUser !== null) {
        const authToken: string = await currentUser.getIdToken();
        const res = await publishTemplateFile(newTemplate, authToken);
        setLoading(false); // End loading
        if (res.success) {
          toast.success(res.message);
          navigate(`/edit/templateFiles`);
        } else {
          toast.error(res.message);
        }
      }
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewTemplate({ ...newTemplate, [name]: value });
  };

  return (
    <>
      <div className="new_activity_container">
        <div className="common_card">
          <div className="top border_tittle">
            <div className="top_bar_tittle">
              <h2>
                <button>
                  <img
                    style={{ width: '1.5rem' }}
                    src={backarrowbtn}
                    alt="backarrowbtn"
                    onClick={() => navigate("/edit/templateFiles")}
                  />
                </button>
                New Template File
              </h2>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="custom_input_flled">
              <label htmlFor="title">Filename</label>
              <input
                type="text"
                id="fileName"
                name="fileName"
                placeholder="Give your file a default name..."
                value={newTemplate.fileName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="custom_input_flled">
              <label htmlFor="fileType">File Type</label>
              <Select
                id="fileType"
                className="react-select-container"
                classNamePrefix="react-select"
                options={fileTypeOptions}
                onChange={handleTemplateFileTypeChange}
                value={fileTypeOptions.find(
                  (option) => option.value === newTemplate.typeId
                )}
                menuPosition="fixed"
                isDisabled={!!newTemplate.formId}
                placeholder="Select..."
              />
            </div>
            <div className="custom_input_flled">
              <label htmlFor="formId">Form (Optional)</label>
              <Select
                id="formId"
                className="react-select-container"
                classNamePrefix="react-select"
                options={formOptions}
                onChange={handleFormChange}
                value={formOptions.find(
                  (option) => option.value === newTemplate.formId
                )}
                menuPosition="fixed"
                isClearable
              />
            </div>
            <div className="form_btn_flx mby_t_24">
              <Button type="submit" disabled={loading}>
                {loading ? "Creating..." : "Create Template"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddTemplateFile;
